import React, { useState, useEffect } from 'react';
import {
    Paper, Typography, Box, TextField, Button, IconButton,
    FormControl, InputLabel, Select, MenuItem, FormGroup,
    FormControlLabel, Checkbox, Alert, Divider, CircularProgress,
    RadioGroup, Radio, Stepper, Step, StepLabel,
} from '@mui/material';
import { CheckCircle, ArrowBack, ArrowForward, Add, Delete, Print, PictureAsPdf } from '@mui/icons-material';
import axios from 'axios';

const API_BASE_URL = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5001/api';

const REQUEST_TYPES = [
    { value: 'permanent',     label: 'Permanent room – Please note the rolling request option has now been removed' },
    { value: 'adhoc_weekday', label: 'Ad hoc room (Weekday)' },
    { value: 'adhoc_weekend', label: 'Ad hoc room (Weekend)' },
    { value: 'cancellation',  label: 'Room cancellation' },
    { value: 'swap',          label: 'Room swap' },
    { value: 'blocking',      label: 'Additional room blocking' },
];

const ROOM_TYPES_STANDARD = [
    'Consulting room with an exam couch',
    'Consulting room with separate exam room',
    'Consulting room only (Exam couch not required)',
    'Exam room (Computer and desk not required)',
    'Virtual workstation',
];

const ROOM_TYPES_ADHOC = [
    'Consulting room with an exam couch',
    'Consulting room with separate exam room',
    'Consulting room only (Exam couch not required)',
    'Consulting room with a dental chair',
    'Exam room (Computer and desk not required)',
    'Virtual workstation',
];

const SUBFORM_TITLES = {
    permanent:     'Permanent Room Request',
    adhoc_weekday: 'Ad Hoc Room Request (Weekday)',
    adhoc_weekend: 'Ad Hoc Room Request (Weekend)',
    cancellation:  'Room Cancellation',
    swap:          'Room Swap',
    blocking:      'Additional Room Blocking',
};

const BLANK_CLINIC_ROW = { specialty: '', clinician_name: '', clinic_code: '', type_of_clinic: '', num_patients: '', slot_length: '' };
const BLANK_NURSING_ROW = { obs_weight: '', chaperones: '', urine_samples: '', stool_samples: '', blood_samples: '', equipment_needed: '', other: '' };

const INITIAL_DETAIL = {
    // Permanent (Step 2)
    perm_different_clinics: '', perm_preferred_clinic: '',
    perm_preferred_room: '', perm_not_remote_reason: '',
    perm_start_date: '', perm_adhoc_possible: '', perm_adhoc_not_suitable_reason: '',
    // Ad hoc weekday (Step 2)
    adhoc_wd_type: '', adhoc_wd_date: '',
    adhoc_wd_clinic_rows: [{ ...BLANK_CLINIC_ROW }],
    // Ad hoc weekend (Step 2)
    adhoc_we_date: '',
    // Cancellation (Step 2)
    cancel_type: '', cancel_specialty: '',
    // Swap (Step 2)
    swap_info: '',
    // Blocking (Step 2 — self-contained with dates/session/rooms)
    block_date_from: '', block_date_to: '', block_session: '',
    block_num_rooms: '', block_preferred_location: '', block_reason: '',
    // Step 3 — Room requirements (shared across all types)
    room_num_rooms: '', room_preferred_location: '',
    room_session: [], room_all_day_fallback: '',
    room_types: [], room_nursing_support: '',
    room_nursing_rows: [{ ...BLANK_NURSING_ROW }],
    room_additional_info: '',
};

// ── Helper components ─────────────────────────────────────────────────────────

function FieldLabel({ children, required }) {
    return (
        <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {children}{required && <span style={{ color: '#d32f2f' }}> *</span>}
        </Typography>
    );
}

function RadioField({ label, name, value, onChange, options, required }) {
    return (
        <Box sx={{ mb: 2.5 }}>
            <FieldLabel required={required}>{label}</FieldLabel>
            <RadioGroup value={value} onChange={(e) => onChange(name, e.target.value)}>
                {options.map(opt => (
                    <FormControlLabel key={opt.value} value={opt.value}
                        control={<Radio size="small" />} label={opt.label} />
                ))}
            </RadioGroup>
        </Box>
    );
}

// ── Step 2 sub-forms ──────────────────────────────────────────────────────────

function PermanentForm({ data, set }) {
    return (
        <>
            <TextField label="Date clinic would start if approved" type="date" required fullWidth
                value={data.perm_start_date} onChange={e => set('perm_start_date', e.target.value)}
                InputLabelProps={{ shrink: true }} sx={{ mb: 2.5, maxWidth: 280 }} />

            <RadioField label="Can the rooms be allocated in different clinics?" required
                name="perm_different_clinics" value={data.perm_different_clinics} onChange={set}
                options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} />

            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, mb: 2.5 }}>
                <TextField label="Preferred clinic" fullWidth value={data.perm_preferred_clinic}
                    onChange={e => set('perm_preferred_clinic', e.target.value)}
                    helperText="Which Outpatient clinic would you prefer?" />
                <TextField label="Preferred room" fullWidth value={data.perm_preferred_room}
                    onChange={e => set('perm_preferred_room', e.target.value)}
                    helperText="Which clinic room would you prefer?" />
            </Box>

            <TextField label="Why can this activity not be carried out remotely?" required
                multiline rows={3} fullWidth sx={{ mb: 2.5 }}
                value={data.perm_not_remote_reason} onChange={e => set('perm_not_remote_reason', e.target.value)} />

            <Box sx={{ mb: 2.5, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                <Typography variant="body2" color="text.secondary">
                    Over 90% of Outpatient clinic rooms are now allocated on a permanent basis and it is becoming
                    increasingly difficult to allocate additional permanent rooms. If we are unable to fulfil the
                    request, OPRA will offer ad hoc rooms as an alternative.
                </Typography>
            </Box>

            <RadioField label="Could ad hoc rooms be used to run this clinic if a permanent room is unavailable?" required
                name="perm_adhoc_possible" value={data.perm_adhoc_possible} onChange={set}
                options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} />

            {data.perm_adhoc_possible === 'no' && (
                <TextField label="Please provide details regarding why ad hoc rooms would not be suitable" required
                    multiline rows={3} fullWidth sx={{ mb: 2.5 }}
                    value={data.perm_adhoc_not_suitable_reason}
                    onChange={e => set('perm_adhoc_not_suitable_reason', e.target.value)} />
            )}
        </>
    );
}

function AdhocWeekdayForm({ data, set, specialties }) {
    const rows = data.adhoc_wd_clinic_rows || [{ ...BLANK_CLINIC_ROW }];

    const updateRow = (index, field, value) => {
        set('adhoc_wd_clinic_rows', rows.map((r, i) => i === index ? { ...r, [field]: value } : r));
    };
    const addRow = () => set('adhoc_wd_clinic_rows', [...rows, { ...BLANK_CLINIC_ROW }]);
    const removeRow = (index) => {
        if (rows.length === 1) return;
        set('adhoc_wd_clinic_rows', rows.filter((_, i) => i !== index));
    };

    return (
        <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Rolling requests have now been removed from the ad hoc room form. Please use the permanent request form instead.
            </Typography>

            <RadioField label="Type of ad hoc room request" required
                name="adhoc_wd_type" value={data.adhoc_wd_type} onChange={set}
                options={[
                    { value: 'one_off', label: 'One off (Single date request)' },
                    { value: 'multiple', label: 'Multiple dates (Request a series of one off clinics under the same clinician and clinic code)' },
                ]} />

            {data.adhoc_wd_type === 'one_off' && (
                <TextField label="Date of clinic" type="date" required
                    value={data.adhoc_wd_date} onChange={e => set('adhoc_wd_date', e.target.value)}
                    fullWidth InputLabelProps={{ shrink: true }} sx={{ mb: 2.5, maxWidth: 280 }} />
            )}

            <Box sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight="bold" sx={{ mb: 1.5 }}>
                    Clinic details <span style={{ color: '#d32f2f' }}>*</span>
                </Typography>

                {rows.map((row, i) => (
                    <Box key={i} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, mb: 2, position: 'relative' }}>
                        {rows.length > 1 && (
                            <IconButton size="small" onClick={() => removeRow(i)}
                                sx={{ position: 'absolute', top: 8, right: 8 }}>
                                <Delete fontSize="small" />
                            </IconButton>
                        )}
                        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, mb: 2 }}>
                            <FormControl required fullWidth>
                                <InputLabel>Specialty</InputLabel>
                                <Select value={row.specialty} label="Specialty"
                                    onChange={e => updateRow(i, 'specialty', e.target.value)}>
                                    {specialties.map(s => (
                                        <MenuItem key={s.id} value={s.name}>{s.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField label="Name of clinician" fullWidth value={row.clinician_name}
                                onChange={e => updateRow(i, 'clinician_name', e.target.value)} />
                            <TextField label="Clinic code" required fullWidth value={row.clinic_code}
                                onChange={e => updateRow(i, 'clinic_code', e.target.value)} />
                        </Box>
                        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' } }}>
                            <FormControl required fullWidth>
                                <InputLabel>Type of clinic</InputLabel>
                                <Select value={row.type_of_clinic} label="Type of clinic"
                                    onChange={e => updateRow(i, 'type_of_clinic', e.target.value)}>
                                    <MenuItem value="F2F">F2F</MenuItem>
                                    <MenuItem value="Virtual">Virtual</MenuItem>
                                    <MenuItem value="Mixed">Mixed</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField label="Number of patients" required fullWidth value={row.num_patients}
                                onChange={e => updateRow(i, 'num_patients', e.target.value)} />
                            <TextField label="Slot length" fullWidth value={row.slot_length}
                                onChange={e => updateRow(i, 'slot_length', e.target.value)} />
                        </Box>
                    </Box>
                ))}

                <Button variant="outlined" startIcon={<Add />} onClick={addRow} sx={{ mb: 3 }}>
                    Add Row
                </Button>
            </Box>
        </>
    );
}

function AdhocWeekendForm({ data, set }) {
    return (
        <>
            <Box sx={{ mb: 2.5, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                <Typography variant="body2" color="text.secondary">
                    Weekend clinics will be cohorted into Clinics 7 and 10 with an additional clinic added if required.
                    Please state if your clinic requires a procedure which cannot be performed in either Clinic 7 or 10.
                    Additional clinics will only be allocated if there is a high demand for space or at the discretion of the OPRA team.
                </Typography>
            </Box>
            <TextField label="Date of clinic" type="date" required fullWidth
                value={data.adhoc_we_date} onChange={e => set('adhoc_we_date', e.target.value)}
                InputLabelProps={{ shrink: true }} sx={{ mb: 2.5, maxWidth: 280 }} />
        </>
    );
}

function CancellationForm({ data, set, specialties }) {
    return (
        <>
            <RadioField label="Type of cancellation" required
                name="cancel_type" value={data.cancel_type} onChange={set}
                options={[
                    { value: 'clinic_room', label: 'Clinic room' },
                    { value: 'virtual_workstation', label: 'Virtual workstation' },
                ]} />
            <FormControl required fullWidth sx={{ mb: 2, maxWidth: 400 }}>
                <InputLabel>Specialty</InputLabel>
                <Select value={data.cancel_specialty}
                    onChange={e => set('cancel_specialty', e.target.value)} label="Specialty">
                    {specialties.map(s => (
                        <MenuItem key={s.id} value={s.name}>{s.name}</MenuItem>
                    ))}
                </Select>
            </FormControl>
        </>
    );
}

function SwapForm({ data, set }) {
    return (
        <TextField label="Please provide details of the room swap required" required
            multiline rows={5} fullWidth sx={{ mb: 2 }}
            value={data.swap_info} onChange={e => set('swap_info', e.target.value)} />
    );
}

function BlockingForm({ data, set }) {
    return (
        <>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, mb: 2.5 }}>
                <TextField label="Date from" type="date" required fullWidth
                    value={data.block_date_from} onChange={e => set('block_date_from', e.target.value)}
                    InputLabelProps={{ shrink: true }} />
                <TextField label="Date to" type="date" required fullWidth
                    value={data.block_date_to} onChange={e => set('block_date_to', e.target.value)}
                    InputLabelProps={{ shrink: true }} />
            </Box>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, mb: 2.5 }}>
                <TextField label="Number of rooms required" type="number" required fullWidth
                    value={data.block_num_rooms} onChange={e => set('block_num_rooms', e.target.value)}
                    inputProps={{ min: 1 }} />
                <TextField label="Preferred clinic and room number (if known)" fullWidth
                    value={data.block_preferred_location}
                    onChange={e => set('block_preferred_location', e.target.value)} />
            </Box>
            <RadioField label="Session" required
                name="block_session" value={data.block_session} onChange={set}
                options={[
                    { value: 'am', label: 'AM' },
                    { value: 'pm', label: 'PM' },
                    { value: 'all_day', label: 'All day' },
                ]} />
            <TextField label="Reason for blocking" required multiline rows={3} fullWidth sx={{ mb: 2 }}
                value={data.block_reason} onChange={e => set('block_reason', e.target.value)} />
        </>
    );
}

// ── Step 3 — Room requirements ────────────────────────────────────────────────

function RoomRequirementsForm({ data, set, requestType }) {
    const showNumRooms = ['permanent', 'adhoc_weekday'].includes(requestType);
    const showSession  = ['permanent', 'adhoc_weekday'].includes(requestType);
    const showRoomTypes = ['permanent', 'adhoc_weekday'].includes(requestType);
    const roomTypeList = requestType === 'permanent' ? ROOM_TYPES_STANDARD : ROOM_TYPES_ADHOC;

    const toggleSession = (val) => {
        const next = data.room_session.includes(val)
            ? data.room_session.filter(v => v !== val)
            : [...data.room_session, val];
        set('room_session', next);
    };

    const toggleRoomType = (val) => {
        const next = data.room_types.includes(val)
            ? data.room_types.filter(v => v !== val)
            : [...data.room_types, val];
        set('room_types', next);
    };

    return (
        <>
            {showNumRooms && (
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, mb: 2.5 }}>
                    <TextField label="Number of rooms required" type="number" required fullWidth
                        value={data.room_num_rooms} onChange={e => set('room_num_rooms', e.target.value)}
                        inputProps={{ min: 1 }} />
                    <TextField label="Preferred clinic and room number (if known)" fullWidth
                        value={data.room_preferred_location}
                        onChange={e => set('room_preferred_location', e.target.value)} />
                </Box>
            )}

            {showSession && (
                <Box sx={{ mb: 2.5 }}>
                    <FieldLabel required>Clinic session</FieldLabel>
                    <FormGroup>
                        {[['am', 'AM'], ['pm', 'PM'], ['all_day', 'All day']].map(([val, lbl]) => (
                            <FormControlLabel key={val}
                                control={<Checkbox size="small" checked={data.room_session.includes(val)} onChange={() => toggleSession(val)} />}
                                label={lbl} />
                        ))}
                    </FormGroup>
                    {data.room_session.includes('all_day') && (
                        <Box sx={{ mt: 1 }}>
                            <RadioField
                                label="If a room cannot be found for an all day clinic please select from the following options"
                                required name="room_all_day_fallback" value={data.room_all_day_fallback} onChange={set}
                                options={[
                                    { value: 'accept_am_or_pm', label: 'Will accept rooms in the AM or PM' },
                                    { value: 'accept_am_only',  label: 'Will accept AM rooms only' },
                                    { value: 'accept_pm_only',  label: 'Will accept PM rooms only' },
                                    { value: 'need_both',       label: 'Can only accept rooms available in the AM and PM' },
                                ]} />
                        </Box>
                    )}
                </Box>
            )}

            {showRoomTypes && (
                <Box sx={{ mb: 2.5 }}>
                    <FieldLabel required>Type of room required</FieldLabel>
                    <FormGroup>
                        {roomTypeList.map(opt => (
                            <FormControlLabel key={opt}
                                control={<Checkbox size="small" checked={data.room_types.includes(opt)} onChange={() => toggleRoomType(opt)} />}
                                label={opt} />
                        ))}
                    </FormGroup>
                </Box>
            )}

            <RadioField label="Is nursing support required?" required
                name="room_nursing_support" value={data.room_nursing_support} onChange={set}
                options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} />

            {data.room_nursing_support === 'yes' && (
                <NursingRequirementsGrid
                    rows={data.room_nursing_rows || [{ ...BLANK_NURSING_ROW }]}
                    set={set} />
            )}

            <TextField label="Is there any other information you would like OPRA to consider?"
                multiline rows={3} fullWidth sx={{ mb: 2 }}
                value={data.room_additional_info}
                onChange={e => set('room_additional_info', e.target.value)} />
        </>
    );
}

const NURSING_YN_FIELDS = [
    { key: 'obs_weight',    label: 'Obs/weight' },
    { key: 'chaperones',    label: 'Chaperones' },
    { key: 'urine_samples', label: 'Urine samples' },
    { key: 'stool_samples', label: 'Stool samples' },
    { key: 'blood_samples', label: 'Blood samples' },
];

function NursingRequirementsGrid({ rows, set }) {
    const updateRow = (index, field, value) => {
        set('room_nursing_rows', rows.map((r, i) => i === index ? { ...r, [field]: value } : r));
    };
    const addRow    = () => set('room_nursing_rows', [...rows, { ...BLANK_NURSING_ROW }]);
    const removeRow = (index) => {
        if (rows.length === 1) return;
        set('room_nursing_rows', rows.filter((_, i) => i !== index));
    };

    return (
        <Box sx={{ mb: 2.5 }}>
            <FieldLabel required>Nursing requirements</FieldLabel>
            {rows.map((row, i) => (
                <Box key={i} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, mb: 1.5, position: 'relative' }}>
                    {rows.length > 1 && (
                        <IconButton size="small" onClick={() => removeRow(i)}
                            sx={{ position: 'absolute', top: 8, right: 8 }}>
                            <Delete fontSize="small" />
                        </IconButton>
                    )}

                    {/* Yes/No dropdowns — 4 per row then 1 in the next row */}
                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, mb: 2 }}>
                        {NURSING_YN_FIELDS.slice(0, 4).map(({ key, label }) => (
                            <FormControl fullWidth key={key}>
                                <InputLabel>{label}</InputLabel>
                                <Select value={row[key]} label={label}
                                    onChange={e => updateRow(i, key, e.target.value)}>
                                    <MenuItem value=""><em>Please select</em></MenuItem>
                                    <MenuItem value="yes">Yes</MenuItem>
                                    <MenuItem value="no">No</MenuItem>
                                </Select>
                            </FormControl>
                        ))}
                    </Box>

                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' } }}>
                        <FormControl fullWidth>
                            <InputLabel>Blood samples</InputLabel>
                            <Select value={row.blood_samples} label="Blood samples"
                                onChange={e => updateRow(i, 'blood_samples', e.target.value)}>
                                <MenuItem value=""><em>Please select</em></MenuItem>
                                <MenuItem value="yes">Yes</MenuItem>
                                <MenuItem value="no">No</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField label="Equipment needed" placeholder="E.g Theatre trolley" fullWidth
                            value={row.equipment_needed}
                            onChange={e => updateRow(i, 'equipment_needed', e.target.value)} />
                        <TextField label="Other" fullWidth
                            value={row.other}
                            onChange={e => updateRow(i, 'other', e.target.value)} />
                    </Box>
                </Box>
            ))}
            <Button variant="outlined" startIcon={<Add />} onClick={addRow} size="small">
                Add Row
            </Button>
        </Box>
    );
}

// ── Print / PDF generation ────────────────────────────────────────────────────

function buildPrintHTML(name, email, requestType, detail) {
    const typeLabel = REQUEST_TYPES.find(rt => rt.value === requestType)?.label || requestType;
    const subTitle  = SUBFORM_TITLES[requestType] || 'Request Details';

    const r = (label, value) =>
        value ? `<tr><td class="lbl">${label}</td><td>${String(value).replace(/\n/g, '<br>')}</td></tr>` : '';

    // Page 2
    let page2 = '';
    if (requestType === 'permanent') {
        page2 = `<table>
            ${r('Start date', detail.perm_start_date)}
            ${r('Different clinics acceptable', detail.perm_different_clinics === 'yes' ? 'Yes' : detail.perm_different_clinics === 'no' ? 'No' : '')}
            ${r('Preferred clinic', detail.perm_preferred_clinic)}
            ${r('Preferred room', detail.perm_preferred_room)}
            ${r('Why cannot be done remotely', detail.perm_not_remote_reason)}
            ${r('Ad hoc rooms acceptable', detail.perm_adhoc_possible === 'yes' ? 'Yes' : detail.perm_adhoc_possible === 'no' ? 'No' : '')}
            ${detail.perm_adhoc_possible === 'no' ? r('Why ad hoc not suitable', detail.perm_adhoc_not_suitable_reason) : ''}
        </table>`;
    } else if (requestType === 'adhoc_weekday') {
        const typeStr = detail.adhoc_wd_type === 'one_off' ? 'One off' : detail.adhoc_wd_type === 'multiple' ? 'Multiple dates' : '';
        page2 = `<table>
            ${r('Request type', typeStr)}
            ${detail.adhoc_wd_date ? r('Date', detail.adhoc_wd_date) : ''}
        </table>`;
        if (detail.adhoc_wd_clinic_rows?.length) {
            page2 += `<h3 style="margin-top:14px">Clinic details</h3>
            <table>
                <thead><tr style="background:#005EB8;color:white"><th>Specialty</th><th>Clinician</th><th>Clinic Code</th><th>Type</th><th>Patients</th><th>Slot Length</th></tr></thead>
                <tbody>${detail.adhoc_wd_clinic_rows.map(row => `<tr>
                    <td>${row.specialty || '—'}</td><td>${row.clinician_name || '—'}</td>
                    <td>${row.clinic_code || '—'}</td><td>${row.type_of_clinic || '—'}</td>
                    <td>${row.num_patients || '—'}</td><td>${row.slot_length || '—'}</td>
                </tr>`).join('')}</tbody>
            </table>`;
        }
    } else if (requestType === 'adhoc_weekend') {
        page2 = `<table>${r('Date', detail.adhoc_we_date)}</table>`;
    } else if (requestType === 'cancellation') {
        const ct = { clinic_room: 'Clinic room', virtual_workstation: 'Virtual workstation' };
        page2 = `<table>
            ${r('Type of cancellation', ct[detail.cancel_type] || detail.cancel_type)}
            ${r('Specialty', detail.cancel_specialty)}
        </table>`;
    } else if (requestType === 'swap') {
        page2 = `<table>${r('Details', detail.swap_info)}</table>`;
    } else if (requestType === 'blocking') {
        const sm = { am: 'AM', pm: 'PM', all_day: 'All day' };
        page2 = `<table>
            ${r('Date from', detail.block_date_from)}
            ${r('Date to', detail.block_date_to)}
            ${r('Number of rooms', detail.block_num_rooms)}
            ${r('Preferred location', detail.block_preferred_location)}
            ${r('Session', sm[detail.block_session] || detail.block_session)}
            ${r('Reason', detail.block_reason)}
        </table>`;
    }

    // Page 3
    const slm = { am: 'AM', pm: 'PM', all_day: 'All day' };
    const flm = {
        accept_am_or_pm: 'Will accept rooms in the AM or PM',
        accept_am_only:  'Will accept AM rooms only',
        accept_pm_only:  'Will accept PM rooms only',
        need_both:       'Can only accept rooms available in the AM and PM',
    };
    const sessionStr = (detail.room_session || []).map(s => slm[s] || s).join(', ') || '—';
    const typesStr   = (detail.room_types   || []).join(', ')                         || '—';

    let page3 = `<table>
        ${detail.room_num_rooms          ? r('Number of rooms required', detail.room_num_rooms) : ''}
        ${detail.room_preferred_location ? r('Preferred clinic / room', detail.room_preferred_location) : ''}
        ${detail.room_session?.length    ? r('Session', sessionStr) : ''}
        ${detail.room_session?.includes('all_day') && detail.room_all_day_fallback
            ? r('All-day fallback', flm[detail.room_all_day_fallback] || detail.room_all_day_fallback) : ''}
        ${detail.room_types?.length      ? r('Room type(s) required', typesStr) : ''}
        ${r('Nursing support required', detail.room_nursing_support === 'yes' ? 'Yes' : detail.room_nursing_support === 'no' ? 'No' : '')}
        ${detail.room_additional_info    ? r('Additional information', detail.room_additional_info) : ''}
    </table>`;

    if (detail.room_nursing_support === 'yes' && detail.room_nursing_rows?.length) {
        const nrRows = detail.room_nursing_rows.map(nr =>
            '<tr>' +
            '<td>' + (nr.obs_weight      || '—') + '</td>' +
            '<td>' + (nr.chaperones      || '—') + '</td>' +
            '<td>' + (nr.urine_samples   || '—') + '</td>' +
            '<td>' + (nr.stool_samples   || '—') + '</td>' +
            '<td>' + (nr.blood_samples   || '—') + '</td>' +
            '<td>' + (nr.equipment_needed || '—') + '</td>' +
            '<td>' + (nr.other           || '—') + '</td>' +
            '</tr>'
        ).join('');
        page3 += '<h3 style="margin-top:12px">Nursing requirements</h3>' +
            '<table><thead><tr style="background:#005EB8;color:white">' +
            '<th>Obs/weight</th><th>Chaperones</th><th>Urine samples</th>' +
            '<th>Stool samples</th><th>Blood samples</th><th>Equipment needed</th><th>Other</th>' +
            '</tr></thead><tbody>' + nrRows + '</tbody></table>';
    }

    return `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<title>OPRA Request — ${name}</title>
<style>
  body{font-family:Arial,sans-serif;max-width:860px;margin:40px auto;color:#333;font-size:13px}
  h1{color:#005EB8;font-size:18px;border-bottom:3px solid #005EB8;padding-bottom:8px;margin-bottom:16px}
  h2{color:#005EB8;font-size:14px;margin-top:28px;border-top:1px solid #ccc;padding-top:10px}
  h3{font-size:13px;margin:12px 0 4px;color:#444}
  .meta{font-size:12px;color:#555;margin-bottom:4px}
  .meta span{font-weight:bold;color:#222}
  table{width:100%;border-collapse:collapse;margin:6px 0 10px}
  td,th{padding:6px 10px;border:1px solid #ddd;vertical-align:top}
  td.lbl{font-weight:bold;color:#555;width:34%;white-space:nowrap}
  tr:nth-child(even) td{background:#f8f8f8}
  @media print{body{margin:20px}}
</style>
</head><body>
<h1>OPRA Request Form — Cambridge University Hospitals NHS Foundation Trust</h1>
<p class="meta">Submitted by: <span>${name}</span> &nbsp;|&nbsp; Email: <span>${email}</span> &nbsp;|&nbsp; Request type: <span>${typeLabel}</span></p>
<h2>Page 2 — ${subTitle}</h2>
${page2}
<h2>Page 3 — Room Requirements</h2>
${page3}
</body></html>`;
}

function openPrint(html) {
    const win = window.open('', '_blank', 'width=920,height=720');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 600);
}

// ── Main component ────────────────────────────────────────────────────────────

function OPRARequestForm() {
    const [step, setStep] = useState(0);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [requestType, setRequestType] = useState('');
    const [detail, setDetail] = useState(INITIAL_DETAIL);
    const [specialties, setSpecialties] = useState([]);
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        axios.get(`${API_BASE_URL}/public/specialties`)
            .then(res => setSpecialties(res.data))
            .catch(() => setSpecialties([]));
    }, []);

    const setField = (field, value) => setDetail(prev => ({ ...prev, [field]: value }));

    const handleNext0 = (e) => { e.preventDefault(); if (!name || !email || !requestType) return; setError(''); setStep(1); };
    const handleNext1 = (e) => { e.preventDefault(); setError(''); setStep(2); };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await axios.post(`${API_BASE_URL}/opra`, { name, email, request_type: requestType, ...detail });
            setSubmitted(true);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to submit request. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = () => openPrint(buildPrintHTML(name, email, requestType, detail));

    if (submitted) {
        return (
            <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
                <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom>Request Submitted</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Your OPRA request has been received. A confirmation email has been sent to <strong>{email}</strong>.
                </Typography>
                <Button variant="outlined" onClick={() => {
                    setStep(0); setName(''); setEmail(''); setRequestType('');
                    setDetail(INITIAL_DETAIL); setSubmitted(false);
                }}>
                    Submit Another Request
                </Button>
            </Paper>
        );
    }

    const subFormProps = { data: detail, set: setField, specialties };
    const STEP_LABELS = ['Request type', SUBFORM_TITLES[requestType] || 'Request details', 'Room requirements'];

    return (
        <Paper elevation={3} sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom>OPRA Request Form</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Cambridge University Hospitals NHS Foundation Trust
            </Typography>

            <Stepper activeStep={step} sx={{ mb: 3 }}>
                {STEP_LABELS.map(label => (
                    <Step key={label}><StepLabel>{label}</StepLabel></Step>
                ))}
            </Stepper>

            <Divider sx={{ mb: 3 }} />

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* ── Step 0: request type ── */}
            {step === 0 && (
                <Box component="form" onSubmit={handleNext0}>
                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, mb: 3 }}>
                        <TextField label="Name" required fullWidth value={name}
                            onChange={e => setName(e.target.value)} autoFocus />
                        <TextField label="Email" type="email" required fullWidth value={email}
                            onChange={e => setEmail(e.target.value)}
                            helperText="Copies of the request will be sent to this email address" />
                    </Box>
                    <Box sx={{ mb: 3 }}>
                        <FieldLabel required>What type of room request would you like to submit?</FieldLabel>
                        <RadioGroup value={requestType} onChange={e => setRequestType(e.target.value)}>
                            {REQUEST_TYPES.map(rt => (
                                <FormControlLabel key={rt.value} value={rt.value}
                                    control={<Radio />} label={rt.label} />
                            ))}
                        </RadioGroup>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button type="submit" variant="contained" size="large"
                            disabled={!name || !email || !requestType} endIcon={<ArrowForward />}>
                            Next
                        </Button>
                    </Box>
                </Box>
            )}

            {/* ── Step 1: request-specific details ── */}
            {step === 1 && (
                <Box component="form" onSubmit={handleNext1}>
                    <Typography variant="h6" sx={{ mb: 2 }}>{SUBFORM_TITLES[requestType]}</Typography>

                    {requestType === 'permanent'     && <PermanentForm {...subFormProps} />}
                    {requestType === 'adhoc_weekday' && <AdhocWeekdayForm {...subFormProps} />}
                    {requestType === 'adhoc_weekend' && <AdhocWeekendForm {...subFormProps} />}
                    {requestType === 'cancellation'  && <CancellationForm {...subFormProps} />}
                    {requestType === 'swap'          && <SwapForm {...subFormProps} />}
                    {requestType === 'blocking'      && <BlockingForm {...subFormProps} />}

                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => setStep(0)}>
                            Back
                        </Button>
                        <Button type="submit" variant="contained" size="large" endIcon={<ArrowForward />}>
                            Next
                        </Button>
                    </Box>
                </Box>
            )}

            {/* ── Step 2: room requirements ── */}
            {step === 2 && (
                <Box component="form" onSubmit={handleSubmit}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Room Requirements</Typography>

                    <RoomRequirementsForm data={detail} set={setField} requestType={requestType} />

                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => setStep(1)}>
                            Back
                        </Button>
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                            <Button variant="outlined" startIcon={<Print />} onClick={handlePrint} type="button">
                                Print
                            </Button>
                            <Button variant="outlined" startIcon={<PictureAsPdf />} onClick={handlePrint} type="button">
                                Download PDF
                            </Button>
                            <Button type="submit" variant="contained" size="large" disabled={loading}
                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}>
                                {loading ? 'Submitting…' : 'Submit'}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            )}
        </Paper>
    );
}

export default OPRARequestForm;
